import { DbModuleFile } from '~src/types/db';
import { MODULES } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import { apiCweb } from './api-cweb';
import BPromise from 'bluebird';
import { saveAs } from 'file-saver';
import { saveFromLink } from '~src/utils/downloader';

export interface LoadModulesParams {
  projectId: string;
}

export interface LoadModulesReturnType {
  task: boolean;
  indication: boolean;
  technical_question: boolean;
  supervision: boolean;
  control: boolean;
  commissioning: boolean;
  d_and_v: boolean;
  afts: boolean;
  eco: boolean;
  archive: boolean;
}

export const loadModules = async (key, { projectId }: LoadModulesParams) => {
  appLog('loadModules ->', {
    projectId,
  });

  const res = await apiCweb.get<LoadModulesReturnType>(
    `/projects/${projectId}/modules`
  );

  return res.data;
};

export interface LoadModuleFilesParams {
  projectId: string;
  skip?: number;
  take?: number;
  module?: MODULES;
  sector?: number;
  category?: number;
  withVersions?: boolean;
}

export const loadModuleFiles = async (
  key,
  {
    projectId,
    skip,
    take,
    module,
    sector,
    category,
    withVersions,
  }: LoadModuleFilesParams
) => {
  appLog('loadModuleFiles ->', {
    projectId,
    skip,
    take,
    module,
    sector,
    category,
    withVersions,
  });

  const res = await apiCweb.get<DbModuleFile[]>(
    `/projects/${projectId}/module_files`,
    {
      params: {
        // take,
        skip,
        module,
        sector,
        category,
      },
    }
  );

  if (!withVersions) return res.data;

  await BPromise.map(res.data, async (file) => {
    file.versions = await loadModuleFilesVersions('loadVersionedForModule', {
      projectId,
      moduleId: file.module,
      versioneModuleFile: file.id,
    });
  });

  return res.data;
};

interface LoadModuleFileVersionsParams {
  projectId: string;
  moduleId: MODULES;
  versioneModuleFile: DbModuleFile['id'];
}

export const loadModuleFilesVersions = async (
  key: string,
  { projectId, moduleId, versioneModuleFile }: LoadModuleFileVersionsParams
) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/module_files/${moduleId}/${versioneModuleFile}`
  );
  return res.data;
};

interface GetLinkModuleFileParams {
  projectId: string;
  moduleFileId: string;
  moduleId: number;
  response_type?: 'stream' | 'base64' | 'content';
}

export const getLinkModuleFile = async ({
  projectId,
  moduleFileId,
  moduleId,
  response_type,
}: GetLinkModuleFileParams) => {
  appLog('loadModuleFileLink => ', {
    projectId,
    moduleFileId,
    moduleId,
    response_type,
  });
  const res = await apiCweb.get(
    `/projects/${projectId}/module_files/${moduleFileId}/download?module=${moduleId}&response_type=${response_type}`
  );

  const blob = base64toBlob(res.data);
  const url = URL.createObjectURL(blob);

  return url;
};

export const getModuleFileTwo = async ({
  projectId,
  moduleFileId,
  moduleId,
  response_type,
}: GetLinkModuleFileParams) => {
  appLog('loadModuleFileLink v2 => ', {
    projectId,
    moduleFileId,
    moduleId,
    response_type,
  });

  try {
    const res = await apiCweb.get(
      `/projects/${projectId}/module_files/${moduleFileId}/download`,
      {
        params: {
          module: moduleId,
          response_type: response_type,
        },
        timeout: 30000,
      }
    );

    // console.log('getModuleFileTwo resdata', res.data);

    const blob = base64toBlob(res.data);
    const url = URL.createObjectURL(blob);

    return url;
  } catch (error) {
    // This error is then caught in your fetchUrlsForChunk function and recorded in your log.
    throw error;
  }
};

const base64toBlob = (data: string) => {
  const bytes = atob(data);
  let length = bytes.length;
  const out = new Uint8Array(length);

  while (length--) {
    out[length] = bytes.charCodeAt(length);
  }

  return new Blob([out], { type: 'application/pdf' });
};

// Module Admins API

export interface ModuleAdminUser {
  id: number;
  email: string;
  full_name: string;
  firm_name: string;
  address: string;
  zipcode: string;
  town: string;
  phone_landcode: string | null;
  phone_nr: string;
  mobile_landcode: string;
  mobile_nr: string | null;
  user_initials: string;
  disabled: boolean;
  workgroup: {
    id: number;
    group_initials: string;
    group_name: string;
    group_job_description: string | null;
  };
}

export interface GetModuleAdminsParams {
  projectId: string;
}

export interface GetModuleAdminParams {
  projectId: string;
  userId: string;
}

export interface SetModuleAdminParams {
  projectId: string;
  userId: string;
}

export interface RevokeModuleAdminParams {
  projectId: string;
  userId: string;
}

// GET /v1/project_settings/{project_id}/module_admins
export const getModuleAdmins = async ({
  projectId,
}: GetModuleAdminsParams): Promise<ModuleAdminUser[]> => {
  appLog('getModuleAdmins ->', { projectId });
  const res = await apiCweb.get<ModuleAdminUser[]>(
    `/project_settings/${projectId}/module_admins`
  );
  return res.data;
};

// GET /v1/project_settings/{project_id}/module_admins/{user_id}
export const getModuleAdmin = async ({
  projectId,
  userId,
}: GetModuleAdminParams): Promise<ModuleAdminUser> => {
  appLog('getModuleAdmin ->', { projectId, userId });
  const res = await apiCweb.get<ModuleAdminUser>(
    `/project_settings/${projectId}/module_admins/${userId}`
  );
  return res.data;
};

// POST /v1/project_settings/{project_id}/module_admins/{user_id}
export const setModuleAdmin = async ({
  projectId,
  userId,
}: SetModuleAdminParams): Promise<void> => {
  appLog('setModuleAdmin ->', { projectId, userId });
  await apiCweb.post(
    `/project_settings/${projectId}/module_admins/${userId}`,
    {}
  );
};

// DELETE /v1/project_settings/{project_id}/module_admins/{user_id}
export const revokeModuleAdmin = async ({
  projectId,
  userId,
}: RevokeModuleAdminParams): Promise<void> => {
  appLog('revokeModuleAdmin ->', { projectId, userId });
  await apiCweb.delete(
    `/project_settings/${projectId}/module_admins/${userId}`,
    { data: {} }
  );
};

// ============================================================
// Classic Registrations API
// ============================================================

export interface ClassicRegistration {
  id: string; // e.g. "task_123", "tq_456", "supv_789", "cont_101"
  module: number; // 1=TASK, 3=TQ, 4=SUPV, 5=CONT
  sector_id: number | null;
  project_id: number;
  filename: string;
  created_at: number; // Unix timestamp
  created_by: number;
  state: number; // 3=AVAILABLE, 6=COMPLETED
  sector_name: string | null;
  created_by_name: string;
  workgroup_name: string;
  workgroup_initials: string;
  user_initials: string;
}

export interface GetClassicRegistrationsParams {
  projectId: string;
  sector_ids?: string; // e.g. "null,79848,79739"
  states?: string; // e.g. "3" or "3,6"
  modules?: string; // e.g. "1,3,4,5"
  sort?: string; // e.g. "desc(created_at)" or "asc(module),desc(created_at)"
  skip?: number;
  take?: number;
}

export interface GetClassicRegistrationsResponse {
  data: ClassicRegistration[];
  totalCount: number;
  skip: number;
  take: number;
}

// GET /v1/projects/{project_id}/classic_registrations
export const getClassicRegistrations = async ({
  projectId,
  sector_ids,
  states,
  modules,
  sort = 'desc(created_at)',
  skip = 0,
  take = 50,
}: GetClassicRegistrationsParams): Promise<GetClassicRegistrationsResponse> => {
  appLog('getClassicRegistrations ->', {
    projectId,
    sector_ids,
    states,
    modules,
    sort,
    skip,
    take,
  });

  const res = await apiCweb.get<ClassicRegistration[]>(
    `/projects/${projectId}/classic_registrations`,
    {
      params: {
        sector_ids,
        states,
        modules,
        sort,
        skip,
        take,
      },
    }
  );

  return {
    data: res.data,
    totalCount: parseInt(res.headers['total-count'] || '0', 10),
    skip: parseInt(res.headers['skip'] || '0', 10),
    take: parseInt(res.headers['take'] || '50', 10),
  };
};

export interface DownloadClassicRegistrationReportParams {
  projectId: string;
  id: string; // e.g. "task_123"
  response_type?: 'stream' | 'url' | 'base';
}

// GET /v1/projects/{project_id}/classic_registrations/{id}/report
export const downloadClassicRegistrationReport = async ({
  projectId,
  id,
  response_type = 'stream',
}: DownloadClassicRegistrationReportParams): Promise<Blob> => {
  appLog('downloadClassicRegistrationReport ->', {
    projectId,
    id,
    response_type,
  });

  const res = await apiCweb.get(
    `/projects/${projectId}/classic_registrations/${id}/report`,
    {
      params: { response_type },
      responseType: 'blob', // Important for stream handling
      timeout: 30000,
    }
  );

  return res.data;
};

// ============================================================
// Project Sector Tree API
// ============================================================

export interface Sector {
  sector_id: number;
  sector_name: string;
  sort: number;
  alt_name: string | null;
}

export interface Subdivision {
  subdivision_id: number;
  subdivision_name: string;
  sort: number;
  sectors: Sector[];
}

export interface Block {
  block_id: number;
  block_name: string;
  sort: number;
  subdivisions: Subdivision[];
}

export interface GetSectorTreeParams {
  projectId: string;
}

// GET /v1/projects/{project_id}/sector_tree
export const getSectorTree = async ({
  projectId,
}: GetSectorTreeParams): Promise<Block[]> => {
  appLog('getSectorTree ->', { projectId });

  const res = await apiCweb.get<Block[]>(`/projects/${projectId}/sector_tree`);

  return res.data;
};
